import React, { useState, useRef, useEffect } from 'react';
import { TextField, Box } from '@mui/material';

interface OtpInputProps {
  length?: number;
  value: string;
  onChange: (value: string) => void;
}

const OtpInput: React.FC<OtpInputProps> = ({ length = 6, value, onChange }) => {
  const [otp, setOtp] = useState<string[]>(Array(length).fill(''));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (value) {
      const valueArray = value.split('').slice(0, length);
      const newOtp = Array(length).fill('');
      valueArray.forEach((val, index) => {
        newOtp[index] = val;
      });
      setOtp(newOtp);
    } else {
      setOtp(Array(length).fill(''));
    }
  }, [value, length]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number,
  ) => {
    const inputValue = e.target.value;
    if (isNaN(Number(inputValue))) return;

    const newOtp = [...otp];
    newOtp[index] = inputValue.substring(inputValue.length - 1);
    setOtp(newOtp);

    const otpString = newOtp.join('');
    onChange(otpString);

    if (inputValue && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text/plain');
    if (isNaN(Number(pastedData))) return;

    const otpValues = pastedData.slice(0, length).split('');

    const newOtp = [...otp];
    otpValues.forEach((value, index) => {
      if (index < length) {
        newOtp[index] = value;
      }
    });

    setOtp(newOtp);

    const otpString = newOtp.join('');
    onChange(otpString);

    const lastFilledIndex = Math.min(otpValues.length, length) - 1;
    const nextEmptyIndex = newOtp.findIndex(val => !val);
    const focusIndex = nextEmptyIndex !== -1 ? nextEmptyIndex : lastFilledIndex;
    inputRefs.current[focusIndex]?.focus();
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
      {otp.map((digit, index) => (
        <TextField
          key={index}
          inputRef={ref => (inputRefs.current[index] = ref)}
          value={digit}
          onChange={e =>
            handleChange(e as React.ChangeEvent<HTMLInputElement>, index)
          }
          onKeyDown={e =>
            handleKeyDown(e as React.KeyboardEvent<HTMLInputElement>, index)
          }
          onPaste={handlePaste}
          inputProps={{
            maxLength: 1,
            inputMode: 'numeric',
            pattern: '[0-9]*',
            style: {
              textAlign: 'center',
              width: '40px',
              height: '40px',
              padding: '8px',
              fontSize: '1.25rem',
              fontWeight: 'bold',
            },
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderRadius: 1,
              },
              '&:hover fieldset': {
                borderColor: 'primary.main',
              },
              '&.Mui-focused fieldset': {
                borderColor: 'primary.main',
              },
            },
          }}
        />
      ))}
    </Box>
  );
};

export default OtpInput;
